import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, ShoppingBag, Trash2, X, Loader2 } from "lucide-react";
import { BookingSent } from "../components/cart/BookingSent";
import { Input } from "../components/ui/input";
import { Eyebrow, MaskLines } from "../components/site/Motion";
import { useCart } from "../context/CartContext";
import { buildMessage, groupByCategory, absUrl } from "../components/cart/Cart";
import { SPECS } from "../lib/catalog";
import { SpecSelect } from "../components/gallery/PhotoCard";
import { api, errMsg, imgUrl } from "../lib/api";
import { toast } from "sonner";
import { RecentlyViewed } from "../components/gallery/RecentlyViewed";

const minDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 3);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const field = "h-12 rounded-xl border-[#D4AF37]/25 bg-[#190317]/60 text-[#FFF8EF] placeholder:text-[#E8D8C8]/60";

const Item = ({ it, n, onRemove, onSpec }) => (
  <motion.li layout initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} className="flex gap-4 rounded-2xl border border-[#D4AF37]/15 bg-[#330B2E]/70 p-3" data-testid={`booking-item-${it.id}`}>
    <img src={imgUrl(it.url)} alt={it.title} className="h-24 w-20 shrink-0 rounded-xl object-cover sm:h-28 sm:w-24" />
    <div className="min-w-0 flex-1 py-1">
      <p className="font-cinzel text-[11px] tracking-[0.2em] text-[#D4AF37]">{String(n).padStart(2, "0")}</p>
      <p className="mt-1 text-sm font-medium text-[#FFF8EF] sm:text-base">{it.title}</p>
      <Link to={`/c/${it.slug}`} className="mt-1 inline-block text-xs text-[#E8D8C8] underline-offset-4 hover:underline">{it.catName}</Link>
      {SPECS[it.slug] && (
        <div className="mt-2 flex items-center gap-2 text-xs text-[#E8D8C8]">
          {SPECS[it.slug].label}:
          <SpecSelect slug={it.slug} value={it.spec || SPECS[it.slug].default} onChange={(v) => onSpec(v)} testId={`booking-spec-${it.id}`} />
        </div>
      )}
    </div>
    <button onClick={onRemove} className="flex h-10 w-10 shrink-0 items-center justify-center self-center rounded-full text-[#ff8fb3] hover:bg-[#240620]" aria-label={`Remove ${it.title}`} data-testid={`booking-remove-${it.id}`}>
      <Trash2 size={17} />
    </button>
  </motion.li>
);

const Sent = BookingSent;

const Empty = () => (
  <div className="flex flex-col items-center gap-5 rounded-[28px] border border-dashed border-[#D4AF37]/30 px-6 py-20 text-center" data-testid="booking-empty">
    <ShoppingBag size={36} className="text-[#D4AF37]" />
    <p className="font-display text-4xl italic text-[#F3E5AB]">Your booking list is empty</p>
    <p className="max-w-md text-[#E8D8C8]">Tap "Book" on any design to add it here. Add as many as you like from different categories.</p>
    <Link to={{ pathname: "/", hash: "collections" }} className="rounded-full bg-gold-foil px-6 py-3 text-sm font-semibold text-[#240620]" data-testid="booking-browse-btn">Browse the collection</Link>
  </div>
);

export default function BookingList() {
  const { items, remove, clear, update } = useCart();
  const [info, setInfo] = useState({ name: "", phone: "", email: "", date: "", venue: "" });
  const [sent, setSent] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setInfo((s) => ({ ...s, [k]: e.target.value }));
  const message = useMemo(() => buildMessage(items, info), [items, info]);
  const groups = Object.entries(groupByCategory(items));
  let n = 0;

  const send = () => {
    if (info.name.trim().length < 2 || info.phone.replace(/\D/g, "").length < 7 || !info.date) {
      toast.error("Please add your name, phone and event date so we have your complete details");
      return;
    }
    if (info.date < minDate()) {
      toast.error("Please pick a date at least three days from today");
      return;
    }
    setBusy(true);
    api.post("/bookings", {
      name: info.name.trim(), phone: info.phone.trim(), email: info.email.trim() || null, event_date: info.date, venue: info.venue.trim(),
      items: items.map((it) => ({ id: it.id, title: it.title, catName: it.catName, url: absUrl(it.url), spec: it.spec || "" })),
    }).then(({ data }) => {
      setSent({ count: items.length, name: info.name.trim(), date: info.date, message, reportUrl: data?.report_url || null });
      clear();
      toast.success("Booking received — now send the picture on WhatsApp");
    }).catch((err) => toast.error(errMsg(err))).finally(() => setBusy(false));
  };

  return (
    <main className="grain relative min-h-screen bg-[#240620] pb-16 pt-[110px] md:pt-[160px]" data-testid="booking-list-page">
      <div className="relative mx-auto max-w-[1300px] px-5 sm:px-8">
        <Eyebrow>Booking list</Eyebrow>
        <h1 className="mt-4 font-display text-5xl font-medium leading-[0.95] text-[#FFF8EF] sm:text-7xl">
          <MaskLines onLoad lines={["Your selections,", <span key="i" className="italic text-gold-foil">all in one place.</span>]} />
        </h1>
        <p className="mt-5 text-[#E8D8C8]" data-testid="booking-summary">
          {items.length} item(s) from {groups.length} categor{groups.length === 1 ? "y" : "ies"} — sent to us together in one WhatsApp message.
        </p>

        <div className="mt-12">
          {sent && items.length === 0 ? <Sent sent={sent} /> : items.length === 0 ? <Empty /> : (
            <div className="grid gap-10 lg:grid-cols-12">
              <div className="space-y-10 lg:col-span-7">
                {groups.map(([cat, list]) => (
                  <section key={cat} data-testid={`booking-group-${list[0].slug}`}>
                    <h2 className="mb-4 flex items-baseline justify-between border-b border-[#D4AF37]/20 pb-2 font-display text-3xl text-[#F3E5AB]">
                      {cat} <span className="font-body text-sm text-[#E8D8C8]">{list.length}</span>
                    </h2>
                    <ul className="space-y-3">
                      <AnimatePresence initial={false}>
                        {list.map((it) => { n += 1; return <Item key={it.id} it={it} n={n} onRemove={() => remove(it.id)} onSpec={(v) => update(it.id, { spec: v })} />; })}
                      </AnimatePresence>
                    </ul>
                  </section>
                ))}
              </div>
              <aside className="lg:col-span-5">
                <div className="space-y-4 rounded-[28px] border border-[#D4AF37]/25 bg-[#330B2E]/80 p-6 backdrop-blur-xl sm:p-8 lg:sticky lg:top-40">
                  <h2 className="font-display text-3xl text-[#FFF8EF]">Your details</h2>
                  <Input value={info.name} onChange={set("name")} placeholder="Your name *" className={field} data-testid="booking-name-input" />
                  <Input type="tel" value={info.phone} onChange={set("phone")} placeholder="Phone / WhatsApp *" className={field} data-testid="booking-phone-input" />
                  <Input type="email" value={info.email} onChange={set("email")} placeholder="Email (optional) — we'll send you your booking picture" className={field} data-testid="booking-email-input" />
                  <Input type="date" min={minDate()} value={info.date} onChange={set("date")} className={`${field} [color-scheme:dark]`} data-testid="booking-date-input" />
                  <Input value={info.venue} onChange={set("venue")} placeholder="Venue / delivery area" className={field} data-testid="booking-venue-input" />
                  <p className="rounded-xl border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-4 py-3 text-sm font-medium text-[#F3E5AB]" data-testid="booking-min-order-notice">Minimum order for home delivery starts at Rs 1,000/- only.</p>
                  <p className="text-xs text-[#E8D8C8]">Three days prior booking required for home delivery. No returns on fresh flowers.</p>
                  <button onClick={send} disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] py-4 text-sm font-semibold text-[#06260f] transition-transform duration-300 hover:scale-[1.02] disabled:opacity-70" data-testid="booking-send-whatsapp">
                    {busy ? <Loader2 size={18} className="animate-spin" /> : <MessageCircle size={18} />} {busy ? "Preparing your booking picture..." : `Send booking (${items.length} item${items.length === 1 ? "" : "s"})`}
                  </button>
                  <button onClick={() => window.confirm("Remove all items from your booking list?") && clear()} className="flex w-full items-center justify-center gap-2 rounded-full border border-[#D4AF37]/30 py-3 text-sm text-[#F3E5AB]" data-testid="booking-clear-btn">
                    <X size={16} /> Clear list
                  </button>
                  <Link to={{ pathname: "/", hash: "collections" }} className="block text-center text-sm text-[#F3E5AB] underline-offset-4 hover:underline" data-testid="booking-add-more">+ Add more designs</Link>
                </div>
              </aside>
            </div>
          )}
        </div>
      </div>
      <div className="relative mt-12"><RecentlyViewed /></div>
    </main>
  );
}
